import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../ui/Button';

interface StepScaffoldProps {
  title: string;
  subtitle?: string;
  /** Paso actual (1-based) y total, para la barra de progreso. */
  step: number;
  totalSteps: number;
  error?: string;
  onBack?: () => void;
  onContinue?: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  isLoading?: boolean;
  children: React.ReactNode;
}

// Calca app/obros/lib/screens/worker_onboarding/step_scaffold.dart: usado por el registro en
// 2 pasos, el wizard de propuesta (3 pasos) y el onboarding de worker (7 pasos).
export const StepScaffold: React.FC<StepScaffoldProps> = ({
  title,
  subtitle,
  step,
  totalSteps,
  error,
  onBack,
  onContinue,
  continueLabel = 'Continuar',
  continueDisabled = false,
  isLoading = false,
  children,
}) => {
  const progress = Math.min(100, Math.round((step / totalSteps) * 100));

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)]">
      <div className="px-4 sm:px-6 pt-4">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm font-medium text-onSurface-mute hover:text-onSurface mb-3"
          >
            <ChevronLeft className="w-4 h-4" />
            Atrás
          </button>
        )}
        <p className="text-xs font-medium text-onSurface-mute mb-1">
          Paso {step} de {totalSteps}
        </p>
        <div className="h-1.5 w-full rounded-full bg-surface-dim overflow-hidden mb-4">
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <h1 className="text-xl font-bold text-onSurface">{title}</h1>
        {subtitle && <p className="text-sm text-onSurface-mute mt-1">{subtitle}</p>}
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
        {error && (
          <div className="mb-4 rounded-xl bg-danger-50 border border-danger-200 px-4 py-3 text-sm text-danger-700">
            {error}
          </div>
        )}
        {children}
      </div>

      {onContinue && (
        <div className="sticky bottom-0 bg-surface border-t border-outlineVarWarm px-4 sm:px-6 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          <Button
            variant="filled"
            pill
            size="xl"
            className="w-full"
            onClick={onContinue}
            disabled={continueDisabled}
            isLoading={isLoading}
          >
            {continueLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
